import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Breadcrumbs,
  Link,
  Divider,
  Paper
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SaveIcon from '@mui/icons-material/Save';
import BusinessIcon from '@mui/icons-material/Business';
import Layout from '../../components/layout/Layout';
import { useSnackbar } from 'notistack';
import companySettingsService from '../../services/companySettingsService';

const CompanySettings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    name: '',
    uen: '',
    addressLine1: '',
    addressLine2: '',
    contact: '',
    email: ''
  });
  const [originalSettings, setOriginalSettings] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  // Load current settings
  useEffect(() => {
    loadSettings();
  }, []);

  // Check for changes
  useEffect(() => {
    const changed = Object.keys(settings).some(key => 
      settings[key] !== originalSettings[key]
    );
    setHasChanges(changed);
  }, [settings, originalSettings]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await companySettingsService.getSettings();
      setSettings(data);
      setOriginalSettings(data);
    } catch (error) {
      enqueueSnackbar('Failed to load company settings', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field) => (event) => {
    setSettings(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const response = await companySettingsService.updateSettings(settings);
      
      if (response.success) {
        setOriginalSettings(settings);
        enqueueSnackbar(response.message, { variant: 'success' });
        
        if (response.note) {
          enqueueSnackbar(response.note, { 
            variant: 'info', 
            autoHideDuration: 8000 
          });
        }
      } else {
        enqueueSnackbar(response.message || 'Failed to update settings', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to save company settings', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(originalSettings);
  };

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/dashboard"
            color="inherit"
            underline="hover"
          >
            Dashboard
          </Link>
          <Typography color="text.primary">Company Settings</Typography>
        </Breadcrumbs>

        {/* Page Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <BusinessIcon sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              Company Settings
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage your company information that appears on invoices and receipts
          </Typography>
        </Box>

        {/* Settings Form */}
        <Paper elevation={2}>
          <CardContent sx={{ p: 4 }}>
            <Grid container spacing={3}>
              {/* Company Name */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  value={settings.name || ''}
                  onChange={handleInputChange('name')}
                  variant="outlined"
                  helperText="This will appear on all invoices and receipts"
                />
              </Grid>

              {/* UEN Number */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="UEN Number"
                  value={settings.uen || ''}
                  onChange={handleInputChange('uen')}
                  variant="outlined"
                  helperText="Unique Entity Number for your company"
                />
              </Grid>

              {/* Address Line 1 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={settings.addressLine1 || ''}
                  onChange={handleInputChange('addressLine1')}
                  variant="outlined"
                  helperText="Street address, building name, unit number"
                />
              </Grid>

              {/* Address Line 2 */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={settings.addressLine2 || ''}
                  onChange={handleInputChange('addressLine2')}
                  variant="outlined"
                  helperText="City, postal code, country"
                />
              </Grid>

              {/* Contact */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Number"
                  value={settings.contact || ''}
                  onChange={handleInputChange('contact')}
                  variant="outlined"
                  helperText="Phone number with country code"
                />
              </Grid>

              {/* Email */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={settings.email || ''}
                  onChange={handleInputChange('email')}
                  variant="outlined"
                  helperText="Company contact email"
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleReset}
                disabled={!hasChanges || saving}
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                onClick={handleSave}
                disabled={!hasChanges || saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>

            {/* Info Alert */}
            {hasChanges && (
              <Alert severity="info" sx={{ mt: 3 }}>
                <Typography variant="body2">
                  <strong>Note:</strong> Changes will be applied to new invoices immediately. 
                  For permanent storage, the application configuration will need to be updated.
                </Typography>
              </Alert>
            )}
          </CardContent>
        </Paper>

        {/* Preview Section */}
        <Paper elevation={2} sx={{ mt: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom>
              Invoice Preview
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              This is how your company information will appear on invoices:
            </Typography>
            
            <Box 
              sx={{ 
                p: 3, 
                border: '1px solid #e0e0e0', 
                borderRadius: 1, 
                backgroundColor: '#fafafa',
                textAlign: 'right'
              }}
            >
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
                {settings.name || 'COMPANY NAME'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                {settings.addressLine1 || 'ADDRESS LINE 1'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                {settings.addressLine2 || 'ADDRESS LINE 2'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                Contact - {settings.contact || 'CONTACT NUMBER'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                Email- {settings.email || 'EMAIL@COMPANY.COM'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'primary.main' }}>
                UEN-{settings.uen || 'UEN_NUMBER'}
              </Typography>
            </Box>
          </CardContent>
        </Paper>
      </Container>
    </Layout>
  );
};

export default CompanySettings; 